import type {
  CompanionHomeData,
  CompanionHomeModel,
  CompanionProofSummary,
  CompanionSavedPlace,
} from "@/lib/companion/home-model";
import type {
  CompanionDecisionAction,
  CompanionDecisionConfidence,
} from "@/lib/companion/decision";
import {
  ELSEWHERE_SCORE_GAP,
  rankCompanionDecisionPlaces,
} from "@/lib/companion/decision";
import {
  buildCompanionFreshness,
  type CompanionFreshnessState,
} from "@/lib/companion/freshness";
import type {
  CompanionDayIntentDaySummary,
  CompanionDayIntentKind,
  CompanionDayIntentSummaryItem,
} from "@/lib/companion/day-intents";
import { buildCompanionWeekRail } from "@/lib/companion/week-rail";
import { buildCompanionAvailabilityFitLine } from "@/lib/companion/availability-fit";
import {
  CALL_SPORT_LABELS,
  isCallSport,
  type CallSport,
} from "@/lib/the-call/types";
import type { CompanionSignupIntentPrefill } from "@/lib/companion/signup-intent";
import { baseLocalDateISO } from "@/lib/companion/bases";
import { localComparisonHref, normaliseLocalSport } from "@/lib/companion/local-comparison-links";
import {
  COMPANION_BRIEFING_ALLOWED_SEND_HOURS,
  type CompanionBriefingAllowedSendHour,
} from "@/lib/companion/briefing-preferences";
import {
  plannerSportFromContextKey,
  sessionPlannerHref,
} from "@/lib/session-planner/account-preferences";
import { isCompanionPisteMapEntrySlug } from "@/lib/maps/piste-map-verification";

export { COMPANION_BRIEFING_ALLOWED_SEND_HOURS };
export { manualLogSheetView } from "@/app/_components/companion/manual-log-state";
export type {
  ManualLogSheetView,
  ManualLogSport,
} from "@/app/_components/companion/manual-log-state";

export type CompanionHomeStateCopy = {
  kicker: string;
  title: string;
  body: string;
  primaryCta: { label: string; href: string };
};

export type CompanionDayBoardCopy = {
  title: string;
  body: string;
  placeLabel: string;
  bestSignalLabel: string;
};

export type CompanionSetupProgress = {
  completed: number;
  total: number;
  isComplete: boolean;
};

export type CompanionWeekIntentSummary = {
  title: string;
  privacyLabel: string;
  emptyLabel: string;
  items: {
    id: CompanionDayIntentKind;
    label: string;
    actionLabel: string;
    activeLabel: string;
    count: number;
    countLabel: string;
    active: boolean;
    displayNames: string[];
    namesLabel: string | null;
  }[];
};

export type CompanionWeekPreviewDay = {
  id: string;
  dayLabel: string;
  dateLabel: string;
  placeSlug: string | null;
  placeName: string | null;
  placeHref: string;
  conditionLabel: string;
  conditionDetail: string;
  conditionTone: "strong" | "useful" | "weak" | "unknown";
  /** Score on the 0-100 display scale for the mini ring, or null to show the text badge. */
  displayScore: number | null;
  intentSummary: CompanionWeekIntentSummary;
};

export type CompanionPlaceRowView = {
  id: string;
  kind: "condition" | "festival";
  name: string;
  region: string | null;
  href: string;
  plannerHref: string | null;
  verdict: string | null;
  score: number | null;
  showScoreChip: boolean;
  sportChips: { key: CallSport; label: string }[];
  followSportChip: { label: string; href: string } | null;
  windowLine: string | null;
  confirmedLabel: string | null;
  staleness: { state: CompanionFreshnessState; label: string } | null;
  disclosure: { label: string; body: string };
  actionState: {
    favouriteId: string;
    spotSlug: string | null;
    currentBaseId: string | null;
    followedCallSports: CallSport[];
    moveTargets: { baseId: string | null; label: string }[];
    canRemove: boolean;
  } | null;
};

export type CompanionPlaceGroupView = {
  key: string;
  heading: string;
  addPlaceLabel: "Add a place";
  addPlaceHref: string;
  places: CompanionPlaceRowView[];
  tripEndedState: {
    tagLabel: "Trip ended";
    keepFollowingLabel: string;
    muteAlertsLabel: "Mute their alerts";
    confirmCopy: string;
    movePlaceIds: string[];
    muteTargets: Array<{ spotSlug: string; sport: CallSport }>;
  } | null;
};

export type CompanionZoneAState = {
  greeting: string;
  dateLine: string;
  availabilityFitContext: {
    bestWindow: string;
    timeZone: string;
  } | null;
  callTakeover: {
    title: string;
    windowCopy: string | null;
    href: string;
  } | null;
  verdict: {
    actionTone: "go-now" | "go-later" | "go-elsewhere" | "not-today";
    confidenceLabel: string;
    freshnessLabel: string;
    score: number | null;
    verdict: string;
    reason: string;
    safetyLine: string;
    confirmedLabel: string | null;
    windowLine: string | null;
    fitLine: string | null;
    nextUsefulLine: string | null;
  };
  downDay: CompanionHomeData["downDay"];
  crossBaseLine: {
    name: string;
    baseName: string;
    score: number;
    href: string;
  } | null;
  nearbyBetterLine: CompanionNearbyBetterLine | null;
  /**
   * At most two "nearby better" lines: same sport as the main pick (or the followed sport),
   * never the main pick itself, one per place, each beating the pick by the 1.2 gap.
   */
  nearbyBetterLines: CompanionNearbyBetterLine[];
  bestPlace: {
    name: string;
    region: string | null;
    score: number | null;
    verdict: string;
    windowLine: string | null;
    disclosure: { label: string; body: string };
    freshnessLabel: string;
    href: string;
    plannerHref: string | null;
  } | null;
};

export type CompanionNearbyBetterLine = {
  name: string;
  sportLabel: string;
  score: number;
  driveMinutes: number;
  href: string;
  /** Catalogue slug and sport key, so a renderer showing a different pick can re-check them. */
  slug?: string | null;
  sportKey?: string | null;
};

export type CompanionAlertsView = {
  briefing:
    | {
        state: "present";
        title: string;
        dateLabel: string;
        href: string;
        readLabel: "Read";
      }
    | {
        state: "empty";
        title: "No briefing yet";
        body: string;
      };
  settings: {
    title: "Morning briefing";
    enabled: boolean;
    enabledToggleLabel: "Send me a Morning Briefing";
    hourLabel: "Send around (local time at each place)";
    selectedHour: CompanionBriefingAllowedSendHour;
    allowedSendHours: readonly CompanionBriefingAllowedSendHour[];
    emailToggleLabel: "Email me the briefing";
    pushToggleLabel: "Push to this device";
    deviceNote: "Allow notifications on this device first";
    promiseLine: "One briefing each day for your followed places at the active base.";
    note: "Briefings stay off until you switch them on. Quiet hours are automatic.";
    emailChecked: boolean;
    pushChecked: boolean;
  };
  call: {
    count: number;
    countLabel: string;
    latestLine: string;
    manageHref: "/companion/calls";
    manageLabel: "Manage";
    hasLatestCall: boolean;
  };
};

export type CompanionIntentSuggestionView = {
  title: "Suggested from Today" | "Saving to Companion";
  body: string;
  applyLabel: string;
  spots: CompanionSignupIntentPrefill["suggestedSpots"];
  sports: CompanionSignupIntentPrefill["suggestedSports"];
  autoSaveSpots: boolean;
} | null;

export function buildCompanionIntentSuggestionView(
  prefill: CompanionSignupIntentPrefill | null,
): CompanionIntentSuggestionView {
  if (!prefill || prefill.suggestedSpots.length === 0) return null;

  const spots = prefill.suggestedSpots;
  const sports = prefill.suggestedSports;
  const spotCopy =
    spots.length === 1 ? "this place" : `these ${spots.length} places`;
  const applySpotCopy =
    spots.length === 1 ? "this place" : `these ${spots.length}`;
  const sportCopy = sports.map((sport) => sport.label.toLowerCase()).join(", ");
  const followCopy = sportCopy ? ` and follow ${sportCopy}` : "";
  const autoSaveSpots = prefill.autoSaveSpots;

  return {
    title: autoSaveSpots ? "Saving to Companion" : "Suggested from Today",
    body: autoSaveSpots
      ? sportCopy
        ? `Save ${spotCopy} and follow ${sportCopy}.`
        : `Save ${spotCopy}. Alerts stay off until you switch them on.`
      : `Save ${spotCopy}${followCopy}.`,
    applyLabel: autoSaveSpots
      ? `Save ${applySpotCopy}`
      : `Save ${applySpotCopy}${followCopy}`,
    spots,
    sports,
    autoSaveSpots,
  };
}

export function buildCompanionAlertsView(
  data: Pick<CompanionHomeData, "latestBriefing" | "latestCall" | "briefingPreferences">,
  model: Pick<CompanionHomeModel, "allSavedPlaces">,
): CompanionAlertsView {
  const callCount = countCompanionCallListPlaces(model.allSavedPlaces);
  const settings = {
    title: "Morning briefing" as const,
    enabled: data.briefingPreferences.briefingEnabled,
    enabledToggleLabel: "Send me a Morning Briefing" as const,
    hourLabel: "Send around (local time at each place)" as const,
    selectedHour: data.briefingPreferences.localSendTime,
    allowedSendHours: COMPANION_BRIEFING_ALLOWED_SEND_HOURS,
    emailToggleLabel: "Email me the briefing" as const,
    pushToggleLabel: "Push to this device" as const,
    deviceNote: "Allow notifications on this device first" as const,
    promiseLine:
      "One briefing each day for your followed places at the active base." as const,
    note:
      "Briefings stay off until you switch them on. Quiet hours are automatic." as const,
    emailChecked: data.briefingPreferences.emailBriefingConsent,
    pushChecked: data.briefingPreferences.pushBriefingConsent,
  };

  return {
    briefing: data.latestBriefing
      ? {
          state: "present",
          title: data.latestBriefing.title,
          dateLabel: formatCompanionBriefingDate(data.latestBriefing.publishedAt),
          href: data.latestBriefing.href,
          readLabel: "Read",
        }
      : {
          state: "empty",
          title: "No briefing yet",
          body: "No briefing yet. Follow a sport on a saved place and in-app briefing summaries will appear here.",
        },
    settings,
    call: {
      count: callCount,
      countLabel:
        callCount === 1
          ? "1 place on your Call list"
          : `${callCount} places on your Call list`,
      latestLine: latestCallLine(data.latestCall),
      manageHref: "/companion/calls",
      manageLabel: "Manage",
      hasLatestCall: data.latestCall !== null,
    },
  };
}

export function countCompanionCallListPlaces(
  places: Pick<CompanionSavedPlace, "callSports" | "isFestival" | "sport">[],
): number {
  return places.filter((place) => {
    if (isFestivalPlace(place)) return false;
    return validCallSports(place.callSports ?? []).length > 0;
  }).length;
}

export function formatCompanionBriefingDate(value: string | null): string {
  if (!value) return "Date unavailable";
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Date unavailable";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(timestamp));
}

export function buildCompanionZoneAState(
  data: CompanionHomeData,
  model: CompanionHomeModel,
  options: { now?: Date } = {},
): CompanionZoneAState {
  const now = options.now ?? new Date();
  const greetingTimeZone = resolveGreetingTimeZone(model);
  const greetingName = data.user?.displayName?.trim() || null;
  const localHour = timeZoneHour(now, greetingTimeZone);
  const greetingBase =
    localHour < 12 ? "Good morning" : localHour < 18 ? "Good afternoon" : "Good evening";
  const decisionPlace = model.decisionPlace;
  const visiblePlace = model.visiblePlace;
  const bestPlace = visiblePlace;
  const verdictTimeZone = resolvePlaceTimeZone(model, decisionPlace ?? visiblePlace);
  const bestPlaceTimeZone = resolvePlaceTimeZone(model, bestPlace);
  const dateLine = new Intl.DateTimeFormat("en-GB", {
    timeZone: greetingTimeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(now);
  const availabilityFitContext = buildAvailabilityFitContext(model);

  const nearbyBetterLines = buildNearbyBetterLines(data, model, now);
  return {
    greeting: greetingName ? `${greetingBase}, ${greetingName}.` : `${greetingBase}.`,
    dateLine,
    availabilityFitContext,
    callTakeover: buildCallTakeover(data, greetingTimeZone, now),
    verdict: {
      actionTone: verdictTone(model.decision.action),
      confidenceLabel: decisionConfidenceLabel(model.trust.confidence),
      freshnessLabel: model.trust.freshnessLabel,
      score: model.decision.score,
      verdict: model.decision.verdict,
      reason: model.decision.reason,
      safetyLine: companionSafetyLine(decisionPlace, visiblePlace, model.trust.freshnessLabel),
      confirmedLabel: confirmedOnGroundLabel(decisionPlace ?? visiblePlace, verdictTimeZone, now),
      windowLine: buildWindowLine(decisionPlace, verdictTimeZone, now),
      fitLine: buildAvailabilityFitLine(data, availabilityFitContext, now),
      nextUsefulLine: buildNextUsefulLine(model, decisionPlace, verdictTimeZone, now),
    },
    downDay: buildDownDayState(data, model),
    crossBaseLine: buildCrossBaseLine(data, model, now),
    nearbyBetterLine: nearbyBetterLines[0] ?? null,
    nearbyBetterLines,
    bestPlace: bestPlace
      ? {
          name: bestPlace.name,
          region: bestPlace.region,
          score: bestPlace.score,
          verdict: bestPlace.verdict ?? "Open conditions for the latest context PeakWave has.",
          windowLine: buildWindowLine(bestPlace, bestPlaceTimeZone, now),
          disclosure: savedPlaceScoreDisclosure(bestPlace),
          freshnessLabel: model.visiblePlaceTrust?.freshnessLabel ?? "No fresh score",
          href: bestPlace.href,
          plannerHref: plannerHrefForPlace(bestPlace),
        }
      : null,
  };
}

function buildAvailabilityFitLine(
  data: CompanionHomeData,
  context: CompanionZoneAState["availabilityFitContext"],
  now: Date,
): string | null {
  if (!context) return null;

  return buildCompanionAvailabilityFitLine({
    windows: data.availabilityWindows,
    bestWindow: context.bestWindow,
    now,
    timeZone: context.timeZone,
  });
}

function buildAvailabilityFitContext(
  model: CompanionHomeModel,
): CompanionZoneAState["availabilityFitContext"] {
  const place = selectAvailabilityFitPlace(model);
  if (!place) return null;

  const bestWindow = currentBestWindow(place);
  if (!bestWindow) return null;

  return {
    bestWindow,
    timeZone: resolvePlaceTimeZone(model, place),
  };
}

function selectAvailabilityFitPlace(
  model: CompanionHomeModel,
): CompanionSavedPlace | null {
  for (const place of [model.decisionPlace, model.visiblePlace]) {
    const bestWindow = place ? currentBestWindow(place) : null;
    if (bestWindow) return place;
  }

  return null;
}

function buildDownDayState(
  data: CompanionHomeData,
  model: CompanionHomeModel,
): CompanionZoneAState["downDay"] {
  if (model.decision.action !== "do-not-bother") return null;
  const regionSlug = model.activeBase?.regionSlug;
  if (!regionSlug) return null;
  if (!data.downDay) return null;
  if (data.downDay.regionSlug !== regionSlug) return null;
  return data.downDay;
}

/** Board-sport family of a Companion sport key, so "sailing-club" matches "sailing". */
function nearbySportFamily(value: string | null | undefined): string | null {
  return normaliseLocalSport(value);
}

/** The main pick's sport, else the sport followed at that place. */
function mainPickSportFamily(place: CompanionSavedPlace): string | null {
  const candidates = [
    place.preferredSport,
    ...(place.callSports ?? []),
    place.sport === "ski" ? "piste" : place.sport === "surf" ? "surf" : null,
  ];
  for (const candidate of candidates) {
    const family = nearbySportFamily(candidate);
    if (family) return family;
  }
  return null;
}

export const NEARBY_BETTER_LIMIT = 2;

function buildNearbyBetterLines(
  data: CompanionHomeData,
  model: CompanionHomeModel,
  now: Date,
): CompanionNearbyBetterLine[] {
  const activeBase = model.activeBase;
  if (!activeBase) return [];

  const activeBasePlaces = data.savedPlaces.filter(
    (place) => !place.isFestival && place.baseId === activeBase.id,
  );
  if (activeBasePlaces.length === 0) return [];

  const activeBest = rankCompanionDecisionPlaces(activeBasePlaces, now).find(
    (place) => place.score !== null,
  );
  if (!activeBest || activeBest.score === null) return [];
  const activeBestScore = activeBest.score;
  const sportFamily = mainPickSportFamily(activeBest);
  const pickSlug = activeBest.slug?.trim() || null;

  const qualifying = data.nearbyCandidates
    .filter((candidate) => candidate.safetyState !== "flagged-red")
    .filter((candidate) => !pickSlug || candidate.slug !== pickSlug)
    .filter((candidate) => !sportFamily || nearbySportFamily(candidate.sportKey) === sportFamily)
    .filter((candidate) => candidate.score - activeBestScore >= ELSEWHERE_SCORE_GAP - 1e-9)
    .sort((left, right) => {
      const scoreDelta = right.score - left.score;
      if (scoreDelta !== 0) return scoreDelta;
      const driveDelta = left.driveMinutes - right.driveMinutes;
      if (driveDelta !== 0) return driveDelta;
      const distanceDelta = left.distanceKm - right.distanceKm;
      if (distanceDelta !== 0) return distanceDelta;
      return left.name.localeCompare(right.name, "en-GB");
    });

  const seen = new Set<string>();
  const lines: CompanionNearbyBetterLine[] = [];
  for (const candidate of qualifying) {
    const key = candidate.slug || candidate.id;
    if (seen.has(key)) continue;
    seen.add(key);
    lines.push({
      name: candidate.name,
      sportLabel: candidate.sportLabel,
      score: candidate.score,
      driveMinutes: candidate.driveMinutes,
      href: candidate.href,
      slug: candidate.slug || null,
      sportKey: candidate.sportKey || null,
    });
    if (lines.length >= NEARBY_BETTER_LIMIT) break;
  }
  return lines;
}

function buildCrossBaseLine(
  data: CompanionHomeData,
  model: CompanionHomeModel,
  now: Date,
): CompanionZoneAState["crossBaseLine"] {
  const activeBase = model.activeBase;
  if (!activeBase) return null;

  const activeBasePlaces = data.savedPlaces.filter(
    (place) => !place.isFestival && place.baseId === activeBase.id,
  );
  const activeBest = rankCompanionDecisionPlaces(activeBasePlaces, now).find(
    (place) => place.score !== null,
  );
  if (!activeBest || activeBest.score === null) return null;

  const otherBasePlaces = data.savedPlaces.filter(
    (place) => !place.isFestival && place.baseId && place.baseId !== activeBase.id,
  );
  const otherBest = rankCompanionDecisionPlaces(otherBasePlaces, now).find(
    (place) => place.score !== null,
  );
  if (!otherBest || otherBest.score === null) return null;

  const scoreGap = otherBest.score - activeBest.score;
  if (scoreGap < ELSEWHERE_SCORE_GAP - 1e-9) return null;

  const baseName = data.bases.find((base) => base.id === otherBest.baseId)?.name ?? null;
  if (!baseName) return null;

  return {
    name: otherBest.name,
    baseName,
    score: otherBest.score,
    href: otherBest.href,
  };
}

export function companionHomeStateCopy(
  model: CompanionHomeModel,
): CompanionHomeStateCopy {
  if (model.status === "anonymous") {
    return {
      kicker: "Free season command centre",
      title: "PeakWave Companion",
      body: "See what is good now, plan the session, track the day, build proof and keep your season story in one free home.",
      primaryCta: model.primaryCta,
    };
  }

  if (model.status === "empty") {
    return {
      kicker: "Your season command centre",
      title: "Set up your Companion",
      body: "Save the mountain or coast places you care about, then PeakWave can turn conditions, Calls, Briefings and tracking into one daily view.",
      primaryCta: model.primaryCta,
    };
  }

  const dayCopy = companionDayBoardCopy(model);
  return {
    kicker: "Your season command centre",
    title: dayCopy.title,
    body: dayCopy.body,
    primaryCta: model.primaryCta,
  };
}

export function companionDayBoardCopy(model: CompanionHomeModel): CompanionDayBoardCopy {
  const places = model.activeMountainOrCoast;
  const bestPlace = places[0] ?? null;
  const placeLabel =
    places.length === 1 ? "1 place watched" : `${places.length} places watched`;

  if (!bestPlace) {
    return {
      title: "Your places today",
      body:
        "Save a mountain, coast or festival place to compare the day ahead without choosing one permanent home base.",
      placeLabel,
      bestSignalLabel: "No saved signal yet",
    };
  }

  const bestSignalLabel = `Best signal: ${bestPlace.name}`;

  if (places.length === 1) {
    return {
      title: `${bestPlace.name} today`,
      body:
        `${bestPlace.name} can be a holiday stop, a season base or simply the place you care about this week. ` +
        "Keep the conditions, plans, crew and record close without making it your only home.",
      placeLabel,
      bestSignalLabel,
    };
  }

  return {
    title: "Your places today",
    body:
      `Compare ${placeList(places)} before deciding where the day is worth spending. ` +
      `${bestPlace.name} has the strongest saved signal right now.`,
    placeLabel,
    bestSignalLabel,
  };
}

export function companionSetupProgress(model: CompanionHomeModel): CompanionSetupProgress {
  const total = model.checklist.length;
  const completed = model.checklist.filter((step) => step.done).length;
  return {
    completed,
    total,
    isComplete: total > 0 && completed === total,
  };
}

export function companionViewState(
  model: CompanionHomeModel,
): "empty" | "setup" | "ready" {
  if (model.status === "empty") return "empty";
  if (model.status !== "ready") return "empty";
  return companionSetupProgress(model).isComplete ? "ready" : "setup";
}

export function buildCompanionWeekPreview(
  model: CompanionHomeModel,
  options: { now?: Date; dayCount?: number } = {},
): CompanionWeekPreviewDay[] {
  const rail = buildCompanionWeekRail(model.mountainOrCoast, {
    ...options,
    timeZone: resolveWeekRailTimeZone(model),
  });

  return rail.days.map((day) => ({
    ...day,
    displayScore: day.displayScore,
    intentSummary: buildIntentSummaryForDay(
      model.dayIntents,
      day.id,
      day.placeSlug,
    ),
  }));
}

export function buildCompanionWeekSummary(
  model: CompanionHomeModel,
  options: { now?: Date; dayCount?: number } = {},
): string {
  return buildCompanionWeekRail(model.mountainOrCoast, {
    ...options,
    timeZone: resolveWeekRailTimeZone(model),
  }).summary;
}

export function buildCompanionPlaceGroupsView(
  model: CompanionHomeModel,
  options: { now?: Date } = {},
): CompanionPlaceGroupView[] {
  const now = options.now ?? new Date();
  const placeById = new Map(model.allSavedPlaces.map((place) => [place.id, place]));

  return model.placeGroups.map((group) => {
    const base = group.baseId
      ? model.bases.find((item) => item.id === group.baseId) ?? null
      : null;

    return {
      key: group.key,
      heading: group.heading,
      addPlaceLabel: "Add a place",
      addPlaceHref: localComparisonHref({ mode: "area", base: base?.id ?? null }),
      places: group.places.flatMap((groupPlace) => {
        const place = placeById.get(groupPlace.id);
        return place ? [buildCompanionPlaceRowView(place, model, now)] : [];
      }),
      tripEndedState: buildTripEndedState(model, group.baseId, now),
    };
  });
}

export function resolvePrimaryHomeBaseId(
  bases: CompanionHomeModel["bases"],
): string | null {
  return bases.find((base) => base.kind === "home" && base.isPrimary)?.id ?? null;
}

export function savedPlaceScoreDisclosure(
  place: Pick<CompanionSavedPlace, "score" | "scoreKind" | "sport">,
): { label: string; body: string } {
  if (place.score === null) {
    if (place.sport === "festival") {
      return {
        label: "Festival window saved",
        body: "Live festival scoring appears when forecast data is close enough.",
      };
    }
    return {
      label: "No fresh score",
      body: "Open conditions for the latest context PeakWave has.",
    };
  }

  if (place.scoreKind === "generic-fallback") {
    return {
      label: "Generic conditions score",
      body: "General location signal, not a personalised Pulse yet.",
    };
  }

  return {
    label: "Sport-specific score",
    body: "Built from the saved sport context for this place.",
  };
}

function buildCompanionPlaceRowView(
  place: CompanionSavedPlace,
  model: CompanionHomeModel,
  now: Date,
): CompanionPlaceRowView {
  const isFestival = isFestivalPlace(place);
  const followedCallSports = isFestival ? [] : validCallSports(place.callSports ?? []);
  const sportChips = followedCallSports.map((sport) => ({
    key: sport,
    label: CALL_SPORT_LABELS[sport],
  }));
  const freshness = buildCompanionFreshness(place.updatedAt, now);

  return {
    id: place.id,
    kind: isFestival ? "festival" : "condition",
    name: place.name,
    region: place.region,
    href: place.href,
    plannerHref: plannerHrefForPlace(place),
    verdict: place.verdict,
    score: place.score,
    showScoreChip: isFestival ? place.score !== null : true,
    sportChips,
    followSportChip:
      !isFestival && sportChips.length === 0
        ? { label: "+ Follow a sport", href: place.href }
        : null,
    windowLine: placeRowWindowLine(place, isFestival),
    confirmedLabel: confirmedGroundTruthLabel(place),
    staleness:
      !isFestival && freshness.state !== "updated-today"
        ? { state: freshness.state, label: freshness.label }
        : null,
    disclosure: savedPlaceScoreDisclosure(place),
    actionState: isFestival
      ? null
      : {
          favouriteId: place.id,
          spotSlug: place.slug ?? null,
          currentBaseId: place.baseId ?? null,
          followedCallSports,
          moveTargets: buildMoveTargets(model, place.baseId ?? null),
          canRemove: Boolean(place.slug),
        },
  };
}

function validCallSports(callSports: string[]): CallSport[] {
  return callSports.filter(isCallSport);
}

function latestCallLine(call: CompanionHomeData["latestCall"]): string {
  if (!call) return "No Calls yet. They are rare by design.";
  const title = call.title.trim();
  const verdict = call.verdict?.trim() || null;
  if (title && verdict) return `${title}: ${verdict}`;
  return title || verdict || "Latest Call ready.";
}

function buildMoveTargets(
  model: CompanionHomeModel,
  currentBaseId: string | null,
): Array<{ baseId: string | null; label: string }> {
  const baseTargets = model.bases
    .filter((base) => base.id !== currentBaseId)
    .map((base) => ({
      baseId: base.id,
      label: `Move to ${base.name}`,
    }));

  if (currentBaseId) {
    return [...baseTargets, { baseId: null, label: "Elsewhere" }];
  }

  return baseTargets;
}

function buildTripEndedState(
  model: CompanionHomeModel,
  baseId: string | null,
  now: Date,
): CompanionPlaceGroupView["tripEndedState"] {
  if (!baseId) return null;

  const base = model.bases.find((item) => item.id === baseId);
  if (!base || base.kind !== "trip" || !base.endsOn) return null;

  const today = baseLocalDateISO(base.timezone, now);
  if (base.endsOn >= today) return null;

  const conditionPlaces = model.allSavedPlaces.filter(
    (place) => !isFestivalPlace(place) && place.baseId === baseId,
  );
  if (conditionPlaces.length === 0) return null;

  const muteTargets = conditionPlaces.flatMap((place) => {
    if (!place.slug) return [];
    return validCallSports(place.callSports ?? []).map((sport) => ({
      spotSlug: place.slug as string,
      sport,
    }));
  });

  return {
    tagLabel: "Trip ended",
    keepFollowingLabel: `Keep following ${conditionPlaces.length} places`,
    muteAlertsLabel: "Mute their alerts",
    confirmCopy:
      `This turns off briefings and The Call for ${conditionPlaces.length} places. ` +
      "They stay saved.",
    movePlaceIds: conditionPlaces.map((place) => place.id),
    muteTargets,
  };
}

function isFestivalPlace(place: Pick<CompanionSavedPlace, "isFestival" | "sport">): boolean {
  return Boolean(place.isFestival) || place.sport === "festival";
}

function placeRowWindowLine(
  place: CompanionSavedPlace,
  isFestival: boolean,
): string | null {
  if (isFestival) return place.bestWindow?.trim() || null;
  const window = currentBestWindow(place);
  return window ? `Best window: ${window}` : null;
}

function currentBestWindow(
  place: Pick<CompanionSavedPlace, "remainingBestWindow" | "bestWindow">,
): string | null {
  if (place.remainingBestWindow !== undefined) {
    return place.remainingBestWindow?.trim() || null;
  }
  return place.bestWindow?.trim() || null;
}

function confirmedGroundTruthLabel(place: CompanionSavedPlace): string | null {
  const agreement = place.groundTruth;
  if (!agreement || agreement.agrees < 2 || agreement.wrongs !== 0) return null;
  return `Confirmed on the ground · ${Math.trunc(agreement.agrees)} today`;
}

export function decisionActionLabel(action: CompanionDecisionAction): string {
  const labels: Record<CompanionDecisionAction, string> = {
    "go-now": "Go now",
    "go-later": "Go later",
    "go-elsewhere": "Go elsewhere",
    "do-not-bother": "Not today",
  };
  return labels[action];
}

export function decisionConfidenceLabel(
  confidence: CompanionDecisionConfidence,
): string {
  const labels: Record<CompanionDecisionConfidence, string> = {
    high: "High confidence",
    medium: "Medium confidence",
    low: "Low confidence",
  };
  return labels[confidence];
}

function resolveWeekRailTimeZone(model: CompanionHomeModel): string {
  if (model.viewedBase && model.viewedBase.id !== model.activeBase?.id) {
    return model.viewedBase.timezone ?? model.activeBase?.timezone ?? firstBaseTimeZone(model);
  }

  return model.activeBase?.timezone ?? firstBaseTimeZone(model);
}

function resolveGreetingTimeZone(model: CompanionHomeModel): string {
  return model.activeBase?.timezone ?? mostRecentBaseTimeZone(model) ?? "UTC";
}

function firstBaseTimeZone(model: CompanionHomeModel): string {
  return model.bases.find((base) => Boolean(base.timezone))?.timezone ?? "UTC";
}

function mostRecentBaseTimeZone(model: CompanionHomeModel): string | null {
  const datedBases = model.bases
    .filter((base) => Boolean(base.timezone))
    .slice()
    .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
  return datedBases[0]?.timezone ?? null;
}

function resolvePlaceTimeZone(
  model: CompanionHomeModel,
  place: CompanionSavedPlace | null,
): string {
  if (place?.baseId) {
    const base = model.bases.find((item) => item.id === place.baseId);
    if (base?.timezone) return base.timezone;
  }

  return resolveGreetingTimeZone(model);
}

export function plannerHrefForPlace(place: CompanionSavedPlace): string | null {
  if (!place.slug) return null;
  if (place.sport === "ski") {
    return isCompanionPisteMapEntrySlug(place.slug)
      ? `/conditions/${place.slug}/planner`
      : null;
  }

  const plannerSport = plannerSportFromContextKey(place.preferredSport ?? place.locationType);
  if (!plannerSport) return null;
  return sessionPlannerHref(place.slug, plannerSport);
}

export function companionPrivateModulesEnabled(model: CompanionHomeModel): boolean {
  return model.status !== "anonymous";
}

function placeList(places: CompanionSavedPlace[]): string {
  const names = places.map((place) => place.name).filter(Boolean);
  if (names.length <= 2) return names.join(" and ");
  const visible = names.slice(0, 3);
  const suffix = names.length > 3 ? ` and ${names.length - 3} more` : "";
  return `${visible.slice(0, -1).join(", ")} and ${visible[visible.length - 1]}${suffix}`;
}

function buildIntentSummaryForDay(
  summaries: CompanionDayIntentDaySummary[],
  date: string,
  placeSlug: string | null,
): CompanionWeekIntentSummary {
  const daySummary = summaries.find(
    (summary) => summary.date === date && summary.placeSlug === placeSlug,
  );
  const byKind = new Map<CompanionDayIntentKind, CompanionDayIntentSummaryItem>(
    daySummary?.items.map((item) => [item.id, item]) ?? [],
  );

  return {
    title: "Who's in",
    privacyLabel: "Crew names stay opt-in",
    emptyLabel: "No one has ticked in yet",
    items: [
      buildIntentItem("ride", byKind.get("ride")),
      buildIntentItem("day-off", byKind.get("day-off")),
      buildIntentItem("event", byKind.get("event")),
    ],
  };
}

function buildCallTakeover(
  data: CompanionHomeData,
  timeZone: string,
  now: Date,
): CompanionZoneAState["callTakeover"] {
  const latestCall = data.latestCall;
  if (!latestCall?.unread || !latestCall.startsAt) return null;

  const todayId = localDateId(now, timeZone);
  const callDateId = callLocalDateId(latestCall.startsAt, timeZone);
  if (!callDateId || callDateId < todayId) return null;

  return {
    title: latestCall.title,
    windowCopy: latestCall.verdict,
    href: latestCall.href,
  };
}

export function companionSafetyLine(
  decisionPlace: CompanionSavedPlace | null,
  visiblePlace: CompanionSavedPlace | null,
  freshnessLabel: string,
): string {
  const firstNote =
    decisionPlace?.safetyNotes?.[0] ??
    visiblePlace?.safetyNotes?.[0] ??
    null;

  if (firstNote) return firstNote;
  const state = decisionPlace?.safetyState ?? visiblePlace?.safetyState ?? "no-data";
  if (state === "clear" && (decisionPlace || visiblePlace) && freshnessLabel === "Updated today") {
    return "No PeakWave safety flags on the data today. Conditions can change faster than forecasts.";
  }
  return "We could not check safety signals for today.";
}

function confirmedOnGroundLabel(
  place: CompanionSavedPlace | null,
  timeZone: string,
  now: Date,
): string | null {
  if (place?.groundTruth && place.groundTruth.agrees >= 2 && place.groundTruth.wrongs === 0) {
    return `Confirmed on the ground · ${place.groundTruth.agrees} today`;
  }

  if (!place?.crowdReports?.length) return null;
  const todayId = localDateId(now, timeZone);
  const count = place.crowdReports.filter((report) => {
    if (!report.agrees || !report.reportedAt) return false;
    return localDateId(new Date(report.reportedAt), timeZone) === todayId;
  }).length;

  if (count < 2) return null;
  return `Confirmed on the ground · ${count} today`;
}

function buildWindowLine(
  place: CompanionSavedPlace | null,
  timeZone: string,
  now: Date,
): string | null {
  if (!place?.bestWindow) return null;
  if (place.remainingBestWindow) return `Best window ${place.remainingBestWindow}`;
  if (place.remainingBestWindow === null) return `Peaked ${place.bestWindow}`;
  const parsed = parseWindowEndHour(place.bestWindow);
  if (parsed === null) return `Best window ${place.bestWindow}`;
  const currentHour = timeZoneHour(now, timeZone);
  return currentHour >= parsed ? `Peaked ${place.bestWindow}` : `Best window ${place.bestWindow}`;
}

function buildNextUsefulLine(
  model: CompanionHomeModel,
  place: CompanionSavedPlace | null,
  timeZone: string,
  now: Date,
): string | null {
  if (!place?.bestWindow) return null;
  const parsed = parseWindowEndHour(place.bestWindow);
  if (parsed === null || timeZoneHour(now, timeZone) < parsed) return null;

  const upcoming = buildCompanionWeekRail(model.activeMountainOrCoast, {
    now,
    timeZone,
  }).days.find(
    (day, index) => index > 0 && day.score !== null && day.score >= 5.5,
  );

  if (!upcoming) return null;

  const weekday = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "long",
  }).format(parseCalendarDate(upcoming.id));
  return `Next useful window: ${weekday}.`;
}

function verdictTone(
  action: CompanionDecisionAction,
): CompanionZoneAState["verdict"]["actionTone"] {
  const tones: Record<CompanionDecisionAction, CompanionZoneAState["verdict"]["actionTone"]> = {
    "go-now": "go-now",
    "go-later": "go-later",
    "go-elsewhere": "go-elsewhere",
    "do-not-bother": "not-today",
  };
  return tones[action];
}

function callLocalDateId(value: string, timeZone: string): string | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return localDateId(new Date(timestamp), timeZone);
}

function localDateId(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(value);
}

function timeZoneHour(value: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    hour12: false,
  }).formatToParts(value);
  return Number(parts.find((part) => part.type === "hour")?.value ?? "0");
}

function parseWindowEndHour(window: string): number | null {
  const match = window.match(/\b(\d{2}):00\s+to\s+(\d{2}):00\b/);
  if (!match) return null;
  return Number(match[2]);
}

function parseCalendarDate(dateISO: string): Date {
  const [year, month, day] = dateISO.split("-").map(Number);
  return new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1));
}

function buildIntentItem(
  id: CompanionDayIntentKind,
  item: CompanionDayIntentSummaryItem | undefined,
): CompanionWeekIntentSummary["items"][number] {
  const count = item?.count ?? 0;
  const displayNames = item?.displayNames ?? [];
  return {
    id,
    label: intentLabel(id),
    actionLabel: intentActionLabel(id),
    activeLabel: intentActiveLabel(id),
    count,
    countLabel: intentCountLabel(id, count),
    active: item?.active ?? false,
    displayNames,
    namesLabel: intentNamesLabel(displayNames, count),
  };
}

function intentLabel(intent: CompanionDayIntentKind): string {
  const labels: Record<CompanionDayIntentKind, string> = {
    ride: "Riding",
    "day-off": "Day off",
    event: "Going",
  };
  return labels[intent];
}

function intentActionLabel(intent: CompanionDayIntentKind): string {
  const labels: Record<CompanionDayIntentKind, string> = {
    ride: "I'm riding",
    "day-off": "I'm off",
    event: "I'm going",
  };
  return labels[intent];
}

function intentActiveLabel(intent: CompanionDayIntentKind): string {
  const labels: Record<CompanionDayIntentKind, string> = {
    ride: "You're riding",
    "day-off": "You're off",
    event: "You're going",
  };
  return labels[intent];
}

function intentCountLabel(intent: CompanionDayIntentKind, count: number): string {
  const suffixes: Record<CompanionDayIntentKind, string> = {
    ride: "riding",
    "day-off": "off",
    event: "going",
  };
  return `${count} ${suffixes[intent]}`;
}

function intentNamesLabel(names: string[], count: number): string | null {
  if (names.length === 0) return null;
  const visible = names.slice(0, 2);
  const hidden = Math.max(0, count - visible.length);
  return hidden > 0 ? `${visible.join(", ")} +${hidden}` : visible.join(", ");
}

export type CompanionRecordCardView = {
  body: string;
  tiles: {
    label: "Days out" | "This month" | "Streak" | "Sports";
    value: string;
    subLabel?: string | null;
  }[];
  actions: (
    | { label: "Track now"; kind: "link"; href: string }
    | { label: "Log a day"; kind: "sheet" }
  )[];
  logShell: {
    title: string;
    body: string;
  };
  groundTruthContribution: string | null;
  trophyShelf: {
    latest: { label: string; isNew: boolean }[];
    allTrophiesHref: string;
    emptyCopy: string | null;
  };
  wrappedLink: { label: "Wrapped"; href: string } | null;
  leaderboardCard: { body: string; href: string } | null;
};

const RECORD_EMPTY_COPY = "Track or log your first day to start the record.";
const NEW_TROPHY_WINDOW_MS = 48 * 60 * 60 * 1000;

export function recordCardView(
  record: CompanionProofSummary,
  options: { now?: Date } = {},
): CompanionRecordCardView {
  const now = options.now ?? new Date();
  const hasProof =
    record.trackedDays > 0 ||
    record.trophies > 0 ||
    record.latestLabel !== null ||
    record.wrappedCards > 0 ||
    record.sportsAllTime > 0;

  return {
    body: hasProof
      ? record.latestLabel ?? "Your tracked days are ready when you want proof."
      : RECORD_EMPTY_COPY,
    tiles: [
      { label: "Days out", value: `${record.trackedDays}` },
      { label: "This month", value: `${record.thisMonthDays}` },
      {
        label: "Streak",
        value: `${record.weeklyStreak} weeks`,
        subLabel: record.bestWeeklyStreak > 0 ? `Best: ${record.bestWeeklyStreak}` : null,
      },
      { label: "Sports", value: `${record.sportsAllTime}` },
    ],
    actions: [
      { label: "Track now", kind: "link", href: "/companion/track" },
      { label: "Log a day", kind: "sheet" },
    ],
    logShell: {
      title: "Log a day",
      body: "Add a day to your private record without GPS.",
    },
    groundTruthContribution:
      typeof record.groundTruthReportCount === "number"
        ? `You have ground-truthed ${record.groundTruthReportCount} forecasts.`
        : null,
    trophyShelf: {
      latest: record.latestTrophies.map((trophy) => ({
        label: trophy.trophyId,
        isNew: isNewTrophy(trophy.awardedAt, now),
      })),
      allTrophiesHref: record.href,
      emptyCopy: hasProof ? null : RECORD_EMPTY_COPY,
    },
    wrappedLink:
      record.wrappedCards > 0
        ? { label: "Wrapped", href: "/companion/wrapped" }
        : null,
    leaderboardCard: {
      body: "Join the leaderboards. Display name only, sessions stay private.",
      href: "/companion/leaderboards",
    },
  };
}

function isNewTrophy(awardedAt: string | null, now: Date): boolean {
  if (!awardedAt) return false;
  const awardedTime = Date.parse(awardedAt);
  if (!Number.isFinite(awardedTime)) return false;
  const age = now.getTime() - awardedTime;
  return age >= 0 && age <= NEW_TROPHY_WINDOW_MS;
}
